import { FATE_KEYWORDS } from "@fate-kingdom/card-data";
import type { VisibleCardViewV1 } from "@fate-kingdom/engine";
import { useEffect, useRef, useState } from "react";

import { AlphaGameIcon } from "../icons/AlphaGameIcon";
import { getAlphaCardPresentation } from "../presentation/alpha-card-presentation";
import { alphaCardUseSummary, formatAlphaResources } from "./alpha-card-utils";

function keywordIdsForCard(card: VisibleCardViewV1): readonly string[] {
  switch (card.type) {
    case "land":
      return ["active-land", "land-action", "found", "develop", "land-stage", "control"];
    case "commander":
      return ["deployment-action", "ready", "spent", "guard", "disband"];
    case "army":
      return ["deployment-action", "army-size", "guard", "upkeep", "disband"];
    case "event":
      return ["event", "event-limit", "discard"];
    default:
      return ["discard"];
  }
}

function CardFacts({ card }: { readonly card: VisibleCardViewV1 }) {
  if (card.type === "land") {
    return (
      <>
        <div>
          <dt>Stage I Food</dt>
          <dd>{card.stages[0].production.food}</dd>
        </div>
        <div>
          <dt>Control</dt>
          <dd>{card.stages[0].maximumControl}</dd>
        </div>
        <div>
          <dt>Development</dt>
          <dd>Stages I–III</dd>
        </div>
      </>
    );
  }
  if (card.type === "commander") {
    return (
      <>
        <div>
          <dt>Attack</dt>
          <dd>{card.attack ?? 0}</dd>
        </div>
        <div>
          <dt>Guard</dt>
          <dd>{card.guard ?? 0}</dd>
        </div>
        {card.armyCapacity === undefined ? null : (
          <div>
            <dt>Historical Capacity</dt>
            <dd>{card.armyCapacity ?? 0}</dd>
          </div>
        )}
      </>
    );
  }
  if (card.type === "army") {
    return (
      <>
        <div>
          <dt>Attack</dt>
          <dd>{card.attack}</dd>
        </div>
        <div>
          <dt>Guard</dt>
          <dd>{card.guard}</dd>
        </div>
        <div>
          <dt>Army Size</dt>
          <dd>{card.armySize}</dd>
        </div>
        <div>
          <dt>Upkeep</dt>
          <dd>{formatAlphaResources(card.upkeep)}</dd>
        </div>
      </>
    );
  }
  return null;
}

export function AlphaCardInspector({
  card,
  cards,
  onClose,
}: {
  readonly card?: VisibleCardViewV1;
  readonly cards?: readonly VisibleCardViewV1[];
  readonly onClose: () => void;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const collection = cards ?? (card === undefined ? [] : [card]);
  const [index, setIndex] = useState(0);
  const activeCard = collection[Math.min(index, Math.max(0, collection.length - 1))];
  const keywords =
    activeCard === undefined
      ? []
      : FATE_KEYWORDS.filter((keyword) => keywordIdsForCard(activeCard).includes(keyword.id));
  const [selectedKeywordId, setSelectedKeywordId] = useState<string | undefined>();
  const selectedKeyword =
    keywords.find((keyword) => keyword.id === selectedKeywordId) ?? keywords[0];

  useEffect(() => {
    closeRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key === "ArrowLeft" && collection.length > 1) {
        event.preventDefault();
        setIndex((current) => Math.max(0, current - 1));
        return;
      }
      if (event.key === "ArrowRight" && collection.length > 1) {
        event.preventDefault();
        setIndex((current) => Math.min(collection.length - 1, current + 1));
        return;
      }
      if (event.key !== "Tab") return;
      const dialog = closeRef.current?.closest('[role="dialog"]');
      const focusable = dialog?.querySelectorAll<HTMLElement>(
        'button:not(:disabled), [href], [tabindex]:not([tabindex="-1"])',
      );
      if (focusable === undefined || focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [collection.length, onClose]);

  if (activeCard === undefined) return null;
  const presentation = getAlphaCardPresentation(activeCard);
  const typeName = presentation.cardType.charAt(0).toUpperCase() + presentation.cardType.slice(1);

  return (
    <aside
      className={`alpha-card-inspector alpha-card-inspector--${presentation.faction}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="alpha-card-inspector-title"
      aria-describedby="alpha-card-inspector-description"
    >
      <div className="alpha-card-inspector__heading">
        <span>
          <AlphaGameIcon name={presentation.accent} aria-hidden="true" /> Card encyclopedia
        </span>
        <button ref={closeRef} className="alpha-link-button" type="button" onClick={onClose}>
          Close
        </button>
      </div>
      <div className="alpha-card-inspector__columns">
        <section className="alpha-card-inspector__card-column">
          <p className="alpha-card-inspector__eyebrow">
            {typeName} · {presentation.faction} · public-beta test set
          </p>
          <h2 id="alpha-card-inspector-title">{presentation.displayName}</h2>
          {collection.length > 1 ? (
            <div className="alpha-card-inspector__browser" aria-label="Formation card browser">
              <button
                type="button"
                disabled={index === 0}
                aria-label="Previous formation card"
                onClick={() => setIndex((current) => Math.max(0, current - 1))}
              >
                ‹
              </button>
              <span>
                Formation card {index + 1} of {collection.length}
              </span>
              <button
                type="button"
                disabled={index >= collection.length - 1}
                aria-label="Next formation card"
                onClick={() => setIndex((current) => Math.min(collection.length - 1, current + 1))}
              >
                ›
              </button>
            </div>
          ) : null}
          <div className="alpha-card-inspector__art">
            {presentation.artUrl === undefined ? (
              <AlphaGameIcon name={presentation.accent} label={presentation.artAlt} />
            ) : (
              <img src={presentation.artUrl} alt={presentation.artAlt} />
            )}
          </div>
          <dl className="alpha-card-inspector__facts">
            <CardFacts card={activeCard} />
          </dl>
          <p id="alpha-card-inspector-description" className="alpha-card-inspector__use">
            <strong>Printed use:</strong> {alphaCardUseSummary(activeCard)}
          </p>
          <p className="alpha-card-inspector__zone">
            <strong>Timing:</strong> the authoritative legal-action set decides when this card can
            act. <strong>Zone:</strong> visible card.
          </p>
          <p className="alpha-card-inspector__flavor">{presentation.flavorText}</p>
        </section>
        <section
          className="alpha-card-inspector__keywords"
          aria-labelledby="alpha-card-keywords-title"
        >
          <p className="alpha-card-inspector__eyebrow">Rules reference</p>
          <h3 id="alpha-card-keywords-title">Keywords used by this card</h3>
          <div className="alpha-keyword-chips" role="list">
            {keywords.map((keyword) => (
              <button
                key={keyword.id}
                type="button"
                role="listitem"
                aria-pressed={selectedKeyword?.id === keyword.id}
                onClick={() => setSelectedKeywordId(keyword.id)}
              >
                {keyword.displayName}
              </button>
            ))}
          </div>
          {selectedKeyword === undefined ? null : (
            <article className="alpha-keyword-definition" aria-live="polite">
              <small>{selectedKeyword.category}</small>
              <h4>{selectedKeyword.displayName}</h4>
              <p>{selectedKeyword.fullDefinition}</p>
              <p>
                <strong>Rule reference:</strong> {selectedKeyword.ruleReference}
              </p>
            </article>
          )}
        </section>
      </div>
    </aside>
  );
}
